import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { projectAccountsActions } from '../actions/project-accounts.actions';
import {
    IAbcpSyncBody,
    IAbcpSyncResult,
    IAccountBindingWrite,
    IClassificationSummary,
    IComparisonPeriod,
    IJournalPeriod,
    IPeriodBounds,
    IProjectAccount,
} from '../models/project-account.model';
import { IStatementLayouts } from '../models/statement-layout.model';
import { ProjectAccountsState, RequestStatus } from '../reducer/project-accounts.reducer';
import {
    selectAccountsSearchTerm,
    selectAccountWrite,
    selectAccountWriteError,
    selectAccountWriteStatus,
    selectBindingError,
    selectBindingStatus,
    selectClassificationOverdue,
    selectClassificationSummary,
    selectClosingCurrency,
    selectClosingOwnPeriod,
    selectClosingPeriod,
    selectComparisonPeriods,
    selectJournalPeriods,
    selectLastErpSyncAt,
    selectMandatoryComparisonPeriod,
    selectMissingComparisonPeriods,
    selectOptionalComparisonPeriods,
    selectProjectAccounts,
    selectProjectAccountsError,
    selectProjectAccountsStatus,
    selectSelectedComparisonPeriods,
    selectStatementLayouts,
    selectStepAccounts,
    selectStepAccountsFor,
    selectStepAccountsStatus,
    selectSyncError,
    selectSyncResult,
    selectSyncStatus,
    selectUnassignedAccounts,
    selectUnassignedCount,
    selectWithZeroAccounts,
} from '../selectors/project-accounts.selectors';

/**
 * The one seam the pages use. Nothing else reaches into the store.
 *
 * Pages get observables and dispatch through methods, so a page never names an action type and the
 * store's shape stays an internal matter of this library.
 */
@Injectable()
export class ProjectAccountsProvider {
    public constructor(private readonly store: Store<ProjectAccountsState>) {}

    // ── Reads ────────────────────────────────────────────────────────────────────────────────────

    public accounts$(): Observable<readonly IProjectAccount[]> {
        return this.store.select(selectProjectAccounts);
    }

    public accountsStatus$(): Observable<RequestStatus> {
        return this.store.select(selectProjectAccountsStatus);
    }

    /**
     * The DEFAULT view of the list: prior year alone, zero accounts left out, whatever the screens show.
     *
     * What the navigation, the Jahresabschluss overview and the Kontenplan guard read — a step's status
     * must not depend on a chip or a toggle.
     */
    public stepAccounts$(): Observable<readonly IProjectAccount[]> {
        return this.store.select(selectStepAccounts);
    }

    /**
     * The first answer of the default list for THIS closing: its rows, or `null` when the read failed.
     * Asks for it if the store does not hold it. For a route guard, which waits for exactly one answer.
     */
    public stepAccountsAnswer$(projectId: string): Observable<readonly IProjectAccount[] | null> {
        this.loadStepAccountsIfNeeded(projectId);

        return this.store.select(selectStepAccountsFor(projectId)).pipe(
            filter((answer): answer is readonly IProjectAccount[] | null => answer !== undefined),
            take(1),
        );
    }

    /**
     * Like `stepAccountsAnswer$`, but from a request made now. For a decision that is about to turn the
     * user away: the list in the store may be a second behind a mapping they have just made.
     */
    public freshStepAccountsAnswer$(projectId: string): Observable<readonly IProjectAccount[] | null> {
        this.reloadStepAccounts(projectId);

        return this.store.select(selectStepAccountsFor(projectId)).pipe(
            filter((answer): answer is readonly IProjectAccount[] | null => answer !== undefined),
            take(1),
        );
    }

    public stepAccountsStatus$(): Observable<RequestStatus> {
        return this.store.select(selectStepAccountsStatus);
    }

    public accountsError$(): Observable<string | null> {
        return this.store.select(selectProjectAccountsError);
    }

    /** Whether the classification stopped answering — the banner's "outstanding" state. */
    public classificationOverdue$(): Observable<boolean> {
        return this.store.select(selectClassificationOverdue);
    }

    public summary$(): Observable<IClassificationSummary | null> {
        return this.store.select(selectClassificationSummary);
    }

    /** The accounts without a position, in number order — the modal's work list. */
    public unassignedAccounts$(): Observable<readonly IProjectAccount[]> {
        return this.store.select(selectUnassignedAccounts);
    }

    public unassignedCount$(): Observable<number> {
        return this.store.select(selectUnassignedCount);
    }

    /** The period the last answer COVERED. Labels the current column of figures. */
    public closingPeriod$(): Observable<IPeriodBounds | null> {
        return this.store.select(selectClosingPeriod);
    }

    /**
     * The period the closing COVERS, from the closing itself — answered before any journal exists.
     *
     * What the sync dialog anchors on. `closingPeriod$` above is null until an account list has
     * reported a period it actually read, which on a closing that never synced is never.
     */
    public closingOwnPeriod$(): Observable<IPeriodBounds | null> {
        return this.store.select(selectClosingOwnPeriod);
    }

    /** The comparison periods the last answer COVERED, with their status. Labels the other columns. */
    public comparisonPeriods$(): Observable<readonly IComparisonPeriod[]> {
        return this.store.select(selectComparisonPeriods);
    }

    /** How the two statements are structured. Null while the backend sends no layout. */
    public statementLayouts$(): Observable<IStatementLayouts | null> {
        return this.store.select(selectStatementLayouts);
    }

    /** The closing's currency from its settings. Null while it has none. */
    public closingCurrency$(): Observable<string | null> {
        return this.store.select(selectClosingCurrency);
    }

    /** What the user has chosen. Empty means the backend picks the prior year itself. */
    public selectedComparisonPeriods$(): Observable<readonly IPeriodBounds[]> {
        return this.store.select(selectSelectedComparisonPeriods);
    }

    /** Every period a journal exists for, the closing's own included. What the sync dialog reads. */
    public journalPeriods$(): Observable<readonly IJournalPeriod[]> {
        return this.store.select(selectJournalPeriods);
    }

    /** When this closing last pulled its ERP. Null when it never has. */
    public lastErpSyncAt$(): Observable<string | null> {
        return this.store.select(selectLastErpSyncAt);
    }

    /** The prior year — always compared against, and therefore never a chip. Null when none exists. */
    public mandatoryComparisonPeriod$(): Observable<IJournalPeriod | null> {
        return this.store.select(selectMandatoryComparisonPeriod);
    }

    /** The periods a user may ADD: everything older than the prior year. These are the chips. */
    public optionalComparisonPeriods$(): Observable<readonly IJournalPeriod[]> {
        return this.store.select(selectOptionalComparisonPeriods);
    }

    /** Asked for and not held. The screen says so rather than showing an empty column. */
    public missingComparisonPeriods$(): Observable<readonly IComparisonPeriod[]> {
        return this.store.select(selectMissingComparisonPeriods);
    }

    /** Whether accounts that are zero everywhere are kept. Off by default, like the route. */
    public withZeroAccounts$(): Observable<boolean> {
        return this.store.select(selectWithZeroAccounts);
    }

    /** The `Konten` tab's search term (REV-22006). Empty while nothing is searched. */
    public searchTerm$(): Observable<string> {
        return this.store.select(selectAccountsSearchTerm);
    }

    public syncStatus$(): Observable<RequestStatus> {
        return this.store.select(selectSyncStatus);
    }

    public syncError$(): Observable<string | null> {
        return this.store.select(selectSyncError);
    }

    /** What the last pull did, per comparison period. Null before the first one. */
    public syncResult$(): Observable<IAbcpSyncResult | null> {
        return this.store.select(selectSyncResult);
    }

    public bindingStatus$(): Observable<RequestStatus> {
        return this.store.select(selectBindingStatus);
    }

    public bindingError$(): Observable<string | null> {
        return this.store.select(selectBindingError);
    }

    /** REV-22005 — the trustee's last account-state write (review flag, document value). */
    public accountWriteStatus$(): Observable<RequestStatus> {
        return this.store.select(selectAccountWriteStatus);
    }

    /** REV-22005 — the backend's message key when that write failed, `null` otherwise. */
    public accountWriteError$(): Observable<string | null> {
        return this.store.select(selectAccountWriteError);
    }

    /** REV-22005 — status and error together, in one emission (see `selectAccountWrite`). */
    public accountWrite$(): Observable<{ status: RequestStatus; error: string | null }> {
        return this.store.select(selectAccountWrite);
    }

    // ── Writes ───────────────────────────────────────────────────────────────────────────────────

    /**
     * Load the list unconditionally, with the backend's DEFAULT view.
     *
     * ⚠️ Screens entering the surface want `loadAccountsIfNeeded()` instead. This one answers the
     * default — prior year alone, zero accounts filtered — so calling it while the user has comparison
     * periods selected replaces their columns with the default ones.
     */
    public loadAccounts(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadAccounts({ projectId }));
    }

    /**
     * What a screen calls when it opens: "show me this closing's accounts."
     *
     * Requests only when the store cannot answer — the list decrypts a journal blob per period, and
     * switching between the balance-sheet and income-statement views of one closing used to pay for
     * that twice for identical rows. Carries the CURRENT comparison periods and zero-account setting
     * with it, which `loadAccounts()` above does not.
     */
    public loadAccountsIfNeeded(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadAccountsIfNeeded({ projectId }));
    }

    /**
     * Fetch the list again now, with the view the screen is on.
     *
     * For a screen that KNOWS the server moved — after a batch of manual assignments. Unlike
     * `loadAccounts()` it keeps the chosen comparison periods, and unlike `loadAccountsIfNeeded()` it
     * never decides the store already knows.
     */
    public reloadAccounts(projectId: string): void {
        this.store.dispatch(projectAccountsActions.reloadAccounts({ projectId }));
    }

    /** "I need the default list": requests only when the store cannot answer. See `stepAccounts$()`. */
    public loadStepAccountsIfNeeded(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadStepAccountsIfNeeded({ projectId }));
    }

    /** Fetch the default list again now — after a change the shared list does not see (the closing settings). */
    public reloadStepAccounts(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadStepAccounts({ projectId }));
    }

    /** The periods a journal exists for. What the comparison chips are built from. */
    public loadJournalPeriods(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadJournalPeriods({ projectId }));
    }

    /**
     * Choose the comparison periods. The list refetches by itself — one more period is one more
     * journal to download and decrypt, and no caller should have to remember that.
     */
    public selectComparisonPeriods(projectId: string, comparisonPeriods: IPeriodBounds[]): void {
        this.store.dispatch(projectAccountsActions.selectComparisonPeriods({ projectId, comparisonPeriods }));
    }

    /**
     * Show or hide accounts that are zero in every period.
     *
     * A refetch, not a local hide: the filter runs in the backend, and this library only ever holds
     * the rows it was given.
     */
    public setWithZeroAccounts(projectId: string, withZeroAccounts: boolean): void {
        this.store.dispatch(projectAccountsActions.setWithZeroAccounts({ projectId, withZeroAccounts }));
    }

    public setSearchTerm(searchTerm: string): void {
        this.store.dispatch(projectAccountsActions.setSearchTerm({ searchTerm }));
    }

    public loadSummary(projectId: string): void {
        this.store.dispatch(projectAccountsActions.loadSummary({ projectId }));
    }

    /** REV-22005 — "Für Review markieren". The reason is what the reviewer reads. */
    public setReviewFlag(projectId: string, accountId: string, reason: string): void {
        this.store.dispatch(
            projectAccountsActions.writeAccountState({ projectId, accountId, command: { kind: 'reviewFlag', reason } }),
        );
    }

    /** REV-22005 — take the review marker off. Idempotent on the server. */
    public clearReviewFlag(projectId: string, accountId: string): void {
        this.store.dispatch(
            projectAccountsActions.writeAccountState({ projectId, accountId, command: { kind: 'reviewUnflag' } }),
        );
    }

    /**
     * REV-22005 — the document value entered by hand (`entscheidregister.md` N-2.2). `amountMinor` is
     * integer minor units in the LEDGER sign; converting from the displayed number is the host's job.
     */
    public enterDocumentBalance(
        projectId: string,
        accountId: string,
        entry: { amountMinor: number; currency: string; note: string | null },
    ): void {
        this.store.dispatch(
            projectAccountsActions.writeAccountState({
                projectId,
                accountId,
                command: { kind: 'documentBalance', ...entry },
            }),
        );
    }

    /** Take the hand-entered document value away: no value again, and the account is open. */
    public clearDocumentBalance(projectId: string, accountId: string): void {
        this.store.dispatch(
            projectAccountsActions.writeAccountState({
                projectId,
                accountId,
                command: { kind: 'documentBalanceClear' },
            }),
        );
    }

    /** REV-22005 — a closed dialog takes its error with it. */
    public resetAccountStateWrite(): void {
        this.store.dispatch(projectAccountsActions.resetAccountStateWrite());
    }

    /** Assign positions. The list refetches on success — the backend derives the resulting state. */
    public writeBindings(projectId: string, bindings: IAccountBindingWrite[]): void {
        this.store.dispatch(projectAccountsActions.writeBindings({ projectId, bindings }));
    }

    /** Pull the ERP for this closing. Company and period come from the project, not from here. */
    public sync(projectId: string, body: IAbcpSyncBody): void {
        this.store.dispatch(projectAccountsActions.sync({ projectId, body }));
    }

    /** REV-22007 — re-import a FILE closing from an uploaded trial balance. Same progress as `sync`. */
    public syncFromFile(projectId: string, fileId: string): void {
        this.store.dispatch(projectAccountsActions.syncFromFile({ projectId, fileId }));
    }

    /** Forget the last pull's outcome once the user has closed it. */
    public resetSync(): void {
        this.store.dispatch(projectAccountsActions.resetSync());
    }
}
